// Offline store for Gunsi Tally: a local mirror of the data (so the app reads
// while offline) + a mutation outbox (so edits made offline are replayed to
// the backend when connectivity returns). Zero-dependency IndexedDB wrapper.
//
// Stores:
//   transactions — keyPath 'id'      : full local mirror of every tx
//   kv           — out-of-line keys  : 'settings', 'categories' snapshots
//   outbox       — keyPath 'key'     : one pending intent per key. For tx ops
//                  key = tx id; for settings/categories key = a fixed marker.
//                  Collapsing by key keeps the queue minimal (create+patches
//                  fold into a single upsert; latest settings/categories win).

const DB_NAME = 'gunsi_tally'
const DB_VER = 1
const S_TX = 'transactions'
const S_KV = 'kv'
const S_OUT = 'outbox'

export const SETTINGS_KEY = '__settings__'
export const CATEGORIES_KEY = '__categories__'

let dbPromise = null

function openDB() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    let req
    try { req = indexedDB.open(DB_NAME, DB_VER) }
    catch (e) { reject(e); return }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(S_TX))  db.createObjectStore(S_TX, { keyPath: 'id' })
      if (!db.objectStoreNames.contains(S_KV))  db.createObjectStore(S_KV)
      if (!db.objectStoreNames.contains(S_OUT)) db.createObjectStore(S_OUT, { keyPath: 'key' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function reqP(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function txDone(t) {
  return new Promise((resolve, reject) => {
    t.oncomplete = () => resolve()
    t.onerror = () => reject(t.error)
    t.onabort = () => reject(t.error)
  })
}

// ── Transactions mirror ────────────────────────────────────────────────────
export async function idbGetAllTx() {
  const db = await openDB()
  return reqP(db.transaction(S_TX).objectStore(S_TX).getAll())
}

export async function idbPutTx(txOrList) {
  const list = Array.isArray(txOrList) ? txOrList : [txOrList]
  const db = await openDB()
  const t = db.transaction(S_TX, 'readwrite')
  const s = t.objectStore(S_TX)
  for (const item of list) if (item?.id) s.put(item)
  return txDone(t)
}

export async function idbDeleteTx(id) {
  const db = await openDB()
  const t = db.transaction(S_TX, 'readwrite')
  t.objectStore(S_TX).delete(id)
  return txDone(t)
}

// Replace the whole mirror atomically (full sync from server).
export async function idbReplaceAllTx(list) {
  const db = await openDB()
  const t = db.transaction(S_TX, 'readwrite')
  const s = t.objectStore(S_TX)
  s.clear()
  for (const item of (list ?? [])) if (item?.id) s.put(item)
  return txDone(t)
}

// ── Key/value snapshots (settings, categories) ─────────────────────────────
export async function idbGetKV(key) {
  const db = await openDB()
  return reqP(db.transaction(S_KV).objectStore(S_KV).get(key))
}

export async function idbSetKV(key, value) {
  const db = await openDB()
  const t = db.transaction(S_KV, 'readwrite')
  t.objectStore(S_KV).put(value, key)
  return txDone(t)
}

// ── Outbox ─────────────────────────────────────────────────────────────────
async function outboxGet(key) {
  const db = await openDB()
  return reqP(db.transaction(S_OUT).objectStore(S_OUT).get(key))
}

async function outboxPut(record) {
  const db = await openDB()
  const t = db.transaction(S_OUT, 'readwrite')
  t.objectStore(S_OUT).put(record)
  return txDone(t)
}

export async function outboxDelete(key) {
  const db = await openDB()
  const t = db.transaction(S_OUT, 'readwrite')
  t.objectStore(S_OUT).delete(key)
  return txDone(t)
}

export async function outboxAll() {
  const db = await openDB()
  return reqP(db.transaction(S_OUT).objectStore(S_OUT).getAll())
}

export async function outboxCount() {
  const db = await openDB()
  return reqP(db.transaction(S_OUT).objectStore(S_OUT).count())
}

async function outboxClear() {
  const db = await openDB()
  const t = db.transaction(S_OUT, 'readwrite')
  t.objectStore(S_OUT).clear()
  return txDone(t)
}

// Intent builders — collapse per key so the queue never grows unbounded.
export async function enqueueCreate(txDoc) {
  if (!txDoc?.id) return
  return outboxPut({ key: txDoc.id, action: 'upsert', tx: txDoc })
}

export async function enqueuePatch(id, patch) {
  const cur = await outboxGet(id)
  if (!cur)                    return outboxPut({ key: id, action: 'patch',  patch })
  if (cur.action === 'upsert') return outboxPut({ key: id, action: 'upsert', tx: { ...cur.tx, ...patch } })
  if (cur.action === 'patch')  return outboxPut({ key: id, action: 'patch',  patch: { ...cur.patch, ...patch } })
  // A delete is already queued for this id — nothing to patch.
}

export async function enqueueDelete(id) {
  const cur = await outboxGet(id)
  // Created offline and never synced → creating then deleting is a no-op.
  if (cur?.action === 'upsert') return outboxDelete(id)
  return outboxPut({ key: id, action: 'delete' })
}

export async function enqueueSettings(settings) {
  return outboxPut({ key: SETTINGS_KEY, action: 'settings', settings })
}

export async function enqueueCategories(categories) {
  return outboxPut({ key: CATEGORIES_KEY, action: 'categories', categories })
}

// Wipe everything (reset / replaceAll).
export async function idbClearAll() {
  await Promise.all([
    idbReplaceAllTx([]),
    outboxClear(),
    idbSetKV(SETTINGS_KEY, null),
    idbSetKV(CATEGORIES_KEY, null),
  ])
}
